import { useEffect, useState } from "react";
import AutoCompleteComponent from "../../../components/common/base-select/autocomplete-component.js.jsx";
import { connect } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NOTIFICATION_CABIN_TYPE } from "../../../constants/cabinType.js";
import { Container, Divider, Grid, Typography } from "@mui/material";
import BaseTitle from "../../../components/common/base-title/base-title.jsx";
import { ArrowLeft2 } from "iconsax-react";
import BaseTextField from "../../../components/common/base-text-field/base-text-field.jsx";
import { BaseButton } from "../../../components/common/base-button/base-button.jsx";
import useNotificationTimeList from "../../../hooks/useNotificationTimeList.js";
import useAirlineList from "../../../hooks/useAirlineList.js";
import { numberWithCommas } from "../../../utils/utility.js";
import useNumberToTextInput from "../../../hooks/useNumberToTextInput.js";
import { useSearchParams } from "react-router-dom";
import { FLIGHT_RESERVE_UNTIL_TIME } from "../../../constants/hours.js";
import { toast } from "react-toastify";
import AvailabilityNotificationResult from "./notification/availability-notification-result.jsx";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls.js";
import { convertToJalali } from "../../../utils/date.js";

function AvailabilityTicketForm({ flightDataList, type, selectedTicket, intFlightDataList }) {
    const accessToken = localStorage.getItem("accessToken");
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation")) || {};
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation")) || {};
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight")) || {};
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight")) || {};
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const selectedReservation = JSON.parse(sessionStorage.getItem("selectedReservation")) || {};
    const { isFlightInternational } = generalInformation;
    const { adult_price } = selectedFlight;
    const { departureDate, departure, destination } = domesticFlightInformation;
    const { item } = selectedReservation;
    const isSelectedReserve = Object.keys(selectedReservation).length > 0;
    const desiredFlightList = isFlightInternational ? intFlightDataList : flightDataList;

    const { t } = useTranslation();
    const [_, setSearchParams] = useSearchParams();
    const { airlines, getAirlines } = useAirlineList();
    const { times, getTimes } = useNotificationTimeList();
    const { rawValue, displayValue, numberText, handleAmountChange } = useNumberToTextInput(item?.max_price);

    useEffect(() => {
        getTimes();
        getAirlines();
    }, []);

    const [selectedTime, setSelectedTime] = useState(item?.departure_time_ranges || []);
    const [selectedAirline, setSelectedAirline] = useState(
        (item?.all_airlines === false && isSelectedReserve && item?.airlines?.map(airline => airline?.id)) || [],
    );

    useEffect(() => {
        if (airlines?.length > 0 && item?.all_airlines === true) {
            setSelectedAirline(airlines);
        }
    }, [airlines, item?.all_airlines]);

    const reserveUntilTimeDefault = isSelectedReserve
        ? FLIGHT_RESERVE_UNTIL_TIME?.find(t => t.id === item?.book_until_amount)
        : FLIGHT_RESERVE_UNTIL_TIME?.[2];

    const [cabinType, setCabinType] = useState(NOTIFICATION_CABIN_TYPE?.[0]);
    const [reserveUntilTime, setReserveUntilTime] = useState(reserveUntilTimeDefault);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [responseData, setResponseData] = useState({});

    useEffect(() => {
        if (!isSelectedReserve) return;
        const cabinKey = item?.cabin_type?.key;

        if (cabinKey === 1) {
            setCabinType(NOTIFICATION_CABIN_TYPE?.[0]);
        } else if (cabinKey === 2) {
            setCabinType(NOTIFICATION_CABIN_TYPE?.[1]);
        } else {
            setCabinType(NOTIFICATION_CABIN_TYPE?.[2]);
        }
    }, []);

    const fieldValues = {
        flightClass: cabinType,
        flightTimes: selectedTime,
        airlines: selectedAirline,
        reserveUntil: reserveUntilTime,
    };

    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
        reset,
    } = useForm({
        mode: "all",
        defaultValues: fieldValues,
    });

    useEffect(() => {
        // for setting default value on the edit process when all the airlines selected
        if (item?.all_airlines) {
            reset(prev => ({
                ...prev,
                airlines: selectedAirline,
            }));
        }
    }, [selectedAirline, reset, item?.all_airlines]);

    const [flightList, setFlightList] = useState(desiredFlightList);
    useEffect(() => {
        setFlightList(desiredFlightList);
    }, [desiredFlightList]);

    const adultPrice = adult_price ?? selectedIntFlight?.fares?.adult?.price;
    const lastFlightItem = flightList?.length - 1;
    const minPrice = numberWithCommas(flightList?.[0]?.adult_price || flightList?.[0]?.fares?.adult?.price);
    const maxPrice = numberWithCommas(flightList?.[lastFlightItem]?.adult_price || flightList?.[lastFlightItem]?.fares?.adult?.price);

    const allSelected = selectedAirline?.length === airlines.length;

    const untilDate = convertToJalali(responseData?.departure_date);
    const lastTimeItem = responseData?.departure_time_ranges?.length - 1;
    const untilTime = responseData?.departure_time ?? responseData?.departure_time_ranges?.[lastTimeItem]?.to_time;

    const formattedTimes = times.map(time => ({
        id: time.id,
        name: `${time.title} - ${time.from_time} - ${time.to_time}`,
    }));

    if (!accessToken) {
        setSearchParams({ action: "login" });
        return;
    }

    const handleRegistration = async () => {
        if (type === "notification") {
            try {
                const data = {
                    departure_time_ranges: selectedTime?.map(item => item.id),
                    airlines: allSelected ? [] : selectedAirline.map(a => a.id),
                    all_airlines: allSelected,
                    cabin_type: cabinType?.id,
                    max_price: rawValue,
                    departure_date: isFlightInternational ? intFlightInformation?.departureDate : departureDate,
                    ...(isFlightInternational
                        ? {
                              departure_airport: intFlightInformation?.departure?.id,
                              destination_airport: intFlightInformation?.destination?.id,
                          }
                        : {
                              departure_city: departure?.id,
                              destination_city: destination?.id,
                          }),
                };

                const response = await axios.post(`${BASE_URL}/notification/manage/`, data, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                if (response.status === 200) {
                    setIsSubmitted(true);
                    setResponseData(response?.data?.data);
                    toast.success(t("requestSubmittedSuccessfully"), {
                        toastId: "custom-id-yes",
                    });
                }
            } catch (error) {
                if (error?.response?.status === 401) {
                    localStorage.clear();
                    setSearchParams({ action: "login" });
                }
                toast.error(t("errorMessage"), { toastId: "custom-id-yes" });
            }
        } else {
            if (!!adultPrice) {
                if (isFlightInternational) {
                    sessionStorage.setItem(
                        "selectedIntFlight",
                        JSON.stringify({
                            ...selectedIntFlight,
                            book_until_amount: reserveUntilTime?.id,
                        }),
                    );
                } else {
                    sessionStorage.setItem(
                        "selectedFlight",
                        JSON.stringify({
                            ...selectedFlight,
                            book_until_amount: reserveUntilTime?.id,
                        }),
                    );
                }
            } else {
                const data = {
                    departure_time_ranges: selectedTime?.map(item => item.id),
                    airlines: allSelected ? [] : isSelectedReserve ? selectedAirline : selectedAirline.map(a => a.id),
                    all_airlines: allSelected,
                    cabin_type: cabinType?.id,
                    max_price: rawValue,
                    departure_date: departureDate,
                    //specific reserve data :
                    book_until_unit: 1,
                    book_until_amount: reserveUntilTime?.id,
                };

                // for automatic reserve
                isFlightInternational
                    ? sessionStorage.setItem("selectedIntFlight", JSON.stringify(data))
                    : sessionStorage.setItem("selectedFlight", JSON.stringify(data));
            }
            toast.success(t("requestSubmittedSuccessfully"), {
                toastId: "custom-id-yes",
            });
        }
    };

    return (
        <Container maxWidth="lg">
            {isSubmitted ? (
                <AvailabilityNotificationResult date={untilDate} hour={untilTime} />
            ) : (
                <Grid component="form" container rowGap={3} onSubmit={handleSubmit(handleRegistration)} sx={{ paddingTop: { xs: 3 } }}>
                    <BaseTitle
                        title={
                            <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                                <ArrowLeft2 /> {t("Flight.Your Ticket")}
                            </Grid>
                        }
                        dividerColor="1px solid var(--medium-gray-color)"
                    />
                    <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
                        {t("Flight.Specify Ticket Details")}
                    </Typography>
                    <Grid
                        container
                        direction="row"
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            rowGap: { xs: 3 },
                        }}
                    >
                        {((!isSelectedReserve && !selectedTicket && type === "reserve") ||
                            type === "notification" ||
                            (isSelectedReserve && item?.departure_time_ranges?.length > 0)) && (//This condition is add to separate the edit forms for two reserve options that one of them is from the selected ticket and another one is a direct reserve
                            <>
                                <Grid container item md={2}>
                                    <AutoCompleteComponent
                                        label={t("flightTime")}
                                        options={formattedTimes}
                                        name="flightTimes"
                                        open={false}
                                        type="other"
                                        control={control}
                                        handleChange={value => setSelectedTime(value)}
                                        multiple
                                        {...register("flightTimes", { required: "اجباری است" })}
                                        errors={errors.flightTimes && errors.flightTimes.message}
                                    />
                                </Grid>
                                <Grid container item md={2}>
                                    <AutoCompleteComponent
                                        label={t("airline")}
                                        options={airlines}
                                        name="airlines"
                                        open={false}
                                        type="other"
                                        control={control}
                                        handleChange={value => setSelectedAirline(value)}
                                        multiple
                                        enableSelectAll
                                        {...register("airlines", { required: "اجباری است" })}
                                        errors={errors.airlines && errors.airlines.message}
                                    />
                                </Grid>
                                <Grid container item md={2}>
                                    <AutoCompleteComponent
                                        label={t("flightClass")}
                                        options={NOTIFICATION_CABIN_TYPE}
                                        name="flightClass"
                                        defaultValue={cabinType}
                                        handleChange={value => setCabinType(value)}
                                        control={control}
                                        disabled={false}
                                        {...register("flightClass", { required: "اجباری است" })}
                                        errors={errors.flightClass && errors.flightClass.message}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    {type === "reserve" && (
                        <Grid container item md={2}>
                            <AutoCompleteComponent
                                label={t("Flight.Reserve Until")}
                                options={FLIGHT_RESERVE_UNTIL_TIME}
                                name="reserveUntil"
                                handleChange={value => setReserveUntilTime(value)}
                                control={control}
                                disabled={false}
                                {...register("reserveUntil", { required: "اجباری است" })}
                                errors={errors.reserveUntil && errors.reserveUntil.message}
                            />
                        </Grid>
                    )}

                    {((!isSelectedReserve && !selectedTicket && type === "reserve") ||
                        type === "notification" ||
                        (isSelectedReserve && item?.departure_time_ranges?.length > 0)) && ( //This condition is add to separate the edit forms for two reserve options that one of them is from the selected ticket and another one is a direct reserve
                        <>
                            <Divider width="100%" />
                            {!isSelectedReserve && (
                                <Grid
                                    container
                                    direction="row"
                                    sx={{
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography variant="body1" sx={{ fontSize: { xs: "0.875rem", md: "1rem" }, color: "var(--orange-color)" }}>
                                        {t("Price.Flight Prices On Selected Date", { minPrice, maxPrice })}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid container item md={2}>
                                <Controller
                                    name="amount"
                                    control={control}
                                    defaultValue={item?.max_price}
                                    rules={{ required: "اجباری است" }}
                                    render={({ field }) => (
                                        <BaseTextField
                                            radius="10px"
                                            label={t("Price.Maximum Price")}
                                            value={displayValue}
                                            setValue={e => {
                                                handleAmountChange(e);
                                                field.onChange(rawValue);
                                            }}
                                            inputProps={{ inputMode: "numeric" }}
                                            error={errors.amount?.message}
                                        />
                                    )}
                                />

                                {numberText && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.secondary",

                                            fontSize: "0.875rem",
                                            fontWeight: 400,
                                            opacity: 0.8,
                                            padding: 3,
                                        }}
                                    >
                                        {numberText}
                                    </Typography>
                                )}
                            </Grid>
                        </>
                    )}

                    <Grid container item md={2} sx={{ ml: { md: 120 } }}>
                        <BaseButton text={t("confirmInformation")} color="primary" size="medium" radius="radius-10" type="submit" />
                    </Grid>
                </Grid>
            )}
        </Container>
    );
}

export default connect(mapStateToProps)(AvailabilityTicketForm);
function mapStateToProps(state) {
    return {
        flightDataList: state.flightData?.data?.list,
        intFlightDataList: state.intFlightData?.results?.list,
        selectedTicket: state.selectedTicket,
    };
}
